import { ContextType } from "react";
import { SessionCrudResult } from "@models/ISchema";
import { AppContext, InitialStateType } from "../context/EditSessionsContext";
import { Types as TypeResult } from "../context/result.reducer";
import { Types as TypeSwitch } from "../context/switchPage.reducer";

type EditorDispatch = ContextType<typeof AppContext>["dispatch"];

interface ApplyValidationResponseParams {
  /** Result of a sessionsCrud dry run, or the validation returned by submitAssignmentProposal. */
  responseData: SessionCrudResult | null | undefined;
  state: InitialStateType;
  dispatch: EditorDispatch;
}

/**
 * Turns a validation result into the reducer actions that open the validation (results) view.
 * Shared by the dry-run response and by a proposal that came back with validation errors, so both
 * render through exactly the same code.
 */
export const applyValidationResponse = ({
  responseData,
  state,
  dispatch,
}: ApplyValidationResponseParams): void => {
  const numberOfSavedSessions = state?.form?.savedSessions?.length ?? 0;
  const numberOfSessionsToCreate = state?.form?.sessionsToCreate?.length ?? 0;
  const numberOfDeletedSessions = Object.keys(state?.form?.sessionsToDelete ?? {}).length;

  if (!(numberOfSavedSessions || numberOfSessionsToCreate || numberOfDeletedSessions)) return;

  const { numberOfEditedSessions, numberOfNewSessions } = (state?.form?.savedSessions ?? []).reduce(
    (acc, session) => ({
      numberOfEditedSessions: acc.numberOfEditedSessions + (session.isNew ? 0 : 1),
      numberOfNewSessions: acc.numberOfNewSessions + (session.isNew ? 1 : 0),
    }),
    { numberOfEditedSessions: 0, numberOfNewSessions: 0 },
  );

  const validateEdited = numberOfEditedSessions > 0;
  const validateCreate = numberOfNewSessions + numberOfSessionsToCreate > 0;
  const validateDeleted = Boolean(responseData?.payload?.deletes?.length);

  if (
    (validateEdited || validateDeleted) &&
    (responseData?.payload?.updates ||
      responseData?.payload?.creates ||
      responseData?.payload?.deletes) &&
    !state?.result?.resultValidation
  ) {
    const { commited, skippedValidations, userCanSkipValidations } = responseData;
    dispatch({
      type: TypeResult.ResultValidation,
      payload: {
        sessionsPayload: [
          ...(responseData?.payload?.updates ?? []),
          ...(responseData?.payload?.deletes ?? []),
        ],
        commited,
        skippedValidations,
        userCanSkipValidations,
      },
    });
  }

  if (validateCreate && responseData && !state?.result?.resultValidation) {
    const { commited, skippedValidations, userCanSkipValidations } = responseData;
    dispatch({
      type: TypeResult.CreateValidation,
      payload: {
        sessionsPayload: [...(responseData?.payload?.creates ?? [])],
        commited,
        skippedValidations,
        userCanSkipValidations,
      },
    });
  }

  if (responseData?.payload?.deletes?.length) {
    dispatch({
      type: TypeResult.DeleteValidation,
      payload: Object.values(state?.form?.sessionsToDelete ?? {}),
    });
  }

  const redirectToResultsView =
    (!!responseData && (validateEdited || validateCreate)) || validateDeleted;

  if (redirectToResultsView) {
    dispatch({ type: TypeResult.SelectCreateSession, payload: null });
    dispatch({ type: TypeSwitch.SetLoading, payload: false });
    dispatch({ type: TypeSwitch.ResultsPage, payload: true });
  }
};
